import { Filesystem, Directory } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'

const DIR = 'recordings'

export interface OrphanRecording {
  name: string
  size: number
}

async function ensureDir(): Promise<void> {
  try {
    await Filesystem.mkdir({ path: DIR, directory: Directory.Data, recursive: true })
  } catch (_) {
    // Already exists or filesystem not available — ignore
  }
}

export async function findOrphanRecordings(): Promise<OrphanRecording[]> {
  if (!Capacitor.isNativePlatform()) return []
  try {
    const result = await Filesystem.readdir({ path: DIR, directory: Directory.Data })
    return result.files
      .filter(f => f.name.endsWith('.webm'))
      .map(f => ({ name: f.name, size: f.size ?? 0 }))
  } catch (_) {
    return []
  }
}

export async function readOrphanBlob(name: string): Promise<Blob> {
  const file = await Filesystem.readFile({
    path: `${DIR}/${name}`,
    directory: Directory.Data,
  })
  const binary = atob(file.data as string)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: 'audio/webm' })
}

export async function discardOrphan(name: string): Promise<void> {
  try {
    await Filesystem.deleteFile({
      path: `${DIR}/${name}`,
      directory: Directory.Data,
    })
  } catch (_) { /* already gone */ }
}

export async function appendChunkToFile(recordingId: string, chunk: Blob): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  await ensureDir()
  const buf = await chunk.arrayBuffer()
  // btoa with String.fromCharCode is OK for chunks up to a few MB. Each chunk
  // is ~32 kbps × 1s = 4 KB, so this is always safe.
  let binary = ''
  const bytes = new Uint8Array(buf)
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  const base64 = btoa(binary)
  await Filesystem.appendFile({
    path: `${DIR}/${recordingId}.webm`,
    data: base64,
    directory: Directory.Data,
  })
}

export async function deleteRecording(recordingId: string): Promise<void> {
  await discardOrphan(`${recordingId}.webm`)
}
